import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import type {IH2hStore, IInvitation} from "data/stores/h2h/h2h.store";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {RequestState, ModalType} from "data/enums";
import {AxiosError} from "axios";
import {extractErrorMessage} from "data/utils";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";

interface IProps {
	invite: IInvitation;
}

export interface IChallengeInviteController extends ViewController<IProps> {
	i18n: ILocalizationStore;
	onAccept: () => void;
	onRemove: () => void;

	get userHasRosters(): boolean;
}

@injectable()
export class ChallengeInviteController implements IChallengeInviteController {
	@observable private _invite?: IInvitation;
	@observable private _requestState?: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore,
		@inject(Bindings.ContestsStore) public readonly _contestsStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IProps) {
		this._invite = param.invite;
	}

	onChange(param: IProps) {
		this.init(param);
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	private get invite() {
		return this._invite;
	}

	get userHasRosters() {
		return this._rosterStore.isRosterFull;
	}

	// onRemove = () => {
	//     const friendId = this.friendId;
	//     this._modalsStore.showModal(ModalType.CONFIRM, {
	//         title: "Are you sure?",
	//         message: "Would you like to remove this friend?",
	//         callback: () => {
	//             this._requestState = RequestState.PENDING;
	//             if( friendId ) {
	//                 this._friendsStore
	//                     .deleteFriend(friendId)
	//                     .then(this.onSuccess)
	//                     .catch(this.onError);
	//                 this._modalsStore.hideModal();
	//             }
	//         },
	//     });
	// };

	private get contestId() {
		return this._contestsStore.currentContest?.id;
	}

	onAccept = () => {
		const invite = this._invite;
		this._requestState = RequestState.PENDING;
		if (invite) {
			this._h2hStore.accept(invite).then(this.onSuccess).catch(this.onError);
		}
	};

	onRemove = () => {
		const invite = this._invite;
		this._requestState = RequestState.PENDING;
		if (invite) {
			this._h2hStore.delete(invite).then(this.onSuccess).catch(this.onError);
		}
	};
	private onSuccess = () => {
		const contestId = this.contestId;
		if (contestId) {
			void Promise.all([
				this._h2hStore.getChallenges(contestId),
				this._h2hStore.getInvitations(contestId),
			]);
		}
		runInAction(() => {
			this._requestState = RequestState.SUCCESS;
		});
	};

	private onError = (error: AxiosError) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
		runInAction(() => {
			this._requestState = RequestState.ERROR;
		});
	};
}
